import { useState } from "react";
import { Trash2, Pencil } from "lucide-react";
import { DynamicIcon } from "./DynamicIcon";
import { CategoryEditDialog } from "./CategoryEditDialog";
import { TableCell, TableRow } from "@/components/ui/table";
import { ActionButton } from "@/components/ui/action-button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useDeleteCategory } from "@/hooks/useCategories";
import { toast } from "sonner";
import type { Category } from "@/types";

interface CategoryRowProps {
  category: Category;
}

export function CategoryRow({ category }: CategoryRowProps) {
  const deleteCategory = useDeleteCategory();
  const [editOpen, setEditOpen] = useState(false);

  const handleDelete = async () => {
    try {
      await deleteCategory.mutateAsync(category.id);
      toast.success("Kategoria usunięta");
    } catch (err) {
      toast.error("Nie udało się usunąć kategorii", {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  const colorHex = category.colorHex.startsWith("#") ? category.colorHex : `#${category.colorHex}`;

  return (
    <>
      <TableRow>
        <TableCell>
          <div className="flex items-center gap-3">
            <div
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
              style={{ backgroundColor: `${colorHex}20` }}
            >
              <DynamicIcon
                name={category.iconName}
                className="h-5 w-5"
                style={{ color: colorHex }}
              />
            </div>
            <span className="font-medium">{category.name}</span>
          </div>
        </TableCell>
        <TableCell className="font-mono text-xs text-muted-foreground">{category.id}</TableCell>
        <TableCell className="text-sm tabular-nums text-muted-foreground">
          #{category.sortOrder}
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-1">
            <ActionButton tooltip="Edytuj" onClick={() => setEditOpen(true)}>
              <Pencil className="h-3.5 w-3.5" />
            </ActionButton>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <ActionButton tooltip="Usuń" className="text-destructive hover:text-destructive">
                  <Trash2 className="h-3.5 w-3.5" />
                </ActionButton>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Usuń kategorię</AlertDialogTitle>
                  <AlertDialogDescription>
                    Czy na pewno chcesz usunąć kategorię „{category.name}”? Tej operacji nie można
                    cofnąć.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Anuluj</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDelete}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Usuń
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </TableCell>
      </TableRow>
      <CategoryEditDialog
        category={editOpen ? category : null}
        open={editOpen}
        onOpenChange={setEditOpen}
      />
    </>
  );
}
